import { db } from "@/firebase";
import { doc, getDoc } from "firebase/firestore";

let activeRequestId = 0;

const toNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const sum = (a, b) => a + b;

const toTrimmedString = (value) =>
  typeof value === "string" ? value.trim() : "";

const formatLocalMonth = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
};

const isoLocalMonth = () => formatLocalMonth(new Date());

const isValidMonth = (value) => /^\d{4}-\d{2}$/.test(String(value || ""));

const normalizeLevelsMap = (levels) => {
  if (!levels || typeof levels !== "object" || Array.isArray(levels)) {
    return {};
  }

  return levels;
};

const normalizeStudentsMap = (students) => {
  const rawList = Array.isArray(students)
    ? students
    : students && typeof students === "object"
      ? Object.values(students)
      : [];

  const out = [];
  const seen = new Set();

  for (const item of rawList) {
    if (!item || typeof item !== "object" || Array.isArray(item)) {
      continue;
    }

    const kidId =
      toTrimmedString(item.kidId) || toTrimmedString(item.studentId);

    const accountId =
      toTrimmedString(item.accountId) || toTrimmedString(item.userId);

    const studentName =
      toTrimmedString(item.studentName) ||
      toTrimmedString(item.name) ||
      kidId ||
      "Unknown Student";

    if (!kidId || seen.has(kidId)) {
      continue;
    }

    seen.add(kidId);

    out.push({
      kidId,
      studentId: kidId, // legacy alias
      accountId,
      studentName,
    });
  }

  return out;
};

const normalizeLevelEntry = (levelKey, levelData) => {
  if (!levelData || typeof levelData !== "object" || Array.isArray(levelData)) {
    return null;
  }

  const levelId =
    toTrimmedString(levelData.levelId) ||
    toTrimmedString(levelData.id) ||
    toTrimmedString(levelKey);

  return {
    id: levelId,
    levelId,
    title: toTrimmedString(levelData.title) || levelId,
    subtitle: toTrimmedString(levelData.subtitle),
    category: levelData.category || "",
    cat: levelData.cat || "",
    group: levelData.group || "",
    tags: Array.isArray(levelData.tags) ? levelData.tags : [],
    sessions: toNumber(levelData.sessions),
    completed: toNumber(levelData.completed),
    totalMs: toNumber(levelData.totalMs),
  };
};

// IMPORTANT:
// This store assumes monthlyStats doc ids are:
//   `${kidId}_${month}`
// Example:
//   abc123_2026-03
//
// If your actual monthlyStats doc id format is different,
// change ONLY this function.
const buildMonthlyStatDocId = (kidId, month) => `${kidId}_${month}`;

const buildEmptyStudentRow = ({ kidId, accountId, studentName, month }) => ({
  id: "",
  userId: accountId || "",
  accountId: accountId || "",
  kidId,
  studentId: kidId, // legacy alias
  studentName,
  month,
  activeDays: 0,
  sessions: 0,
  completed: 0,
  totalMs: 0,
  createdAt: null,
  updatedAt: null,
  levels: {},
});

const buildStatsByName = (rows) => {
  const grouped = {};

  for (const row of rows) {
    const name = row.studentName || row.kidId || "Unknown Student";

    if (!grouped[name]) {
      grouped[name] = {
        name,
        kidIds: [],
        studentIds: [], // legacy alias
        month: row.month || "",
        activeDays: 0,
        sessions: 0,
        completed: 0,
        totalMs: 0,
        levels: {},
      };
    }

    if (row.kidId && !grouped[name].kidIds.includes(row.kidId)) {
      grouped[name].kidIds.push(row.kidId);
    }

    if (row.kidId && !grouped[name].studentIds.includes(row.kidId)) {
      grouped[name].studentIds.push(row.kidId);
    }

    grouped[name].activeDays += toNumber(row.activeDays);
    grouped[name].sessions += toNumber(row.sessions);
    grouped[name].completed += toNumber(row.completed);
    grouped[name].totalMs += toNumber(row.totalMs);

    for (const [levelKey, rawLevelData] of Object.entries(
      normalizeLevelsMap(row.levels)
    )) {
      const levelData = normalizeLevelEntry(levelKey, rawLevelData);
      if (!levelData) continue;

      if (!grouped[name].levels[levelData.id]) {
        grouped[name].levels[levelData.id] = {
          id: levelData.id,
          levelId: levelData.levelId,
          title: levelData.title,
          subtitle: levelData.subtitle,
          category: levelData.category,
          cat: levelData.cat,
          group: levelData.group,
          tags: levelData.tags,
          sessions: 0,
          completed: 0,
          totalMs: 0,
        };
      }

      grouped[name].levels[levelData.id].sessions += levelData.sessions;
      grouped[name].levels[levelData.id].completed += levelData.completed;
      grouped[name].levels[levelData.id].totalMs += levelData.totalMs;

      if (levelData.title) grouped[name].levels[levelData.id].title = levelData.title;
      if (levelData.subtitle) grouped[name].levels[levelData.id].subtitle = levelData.subtitle;
      if (levelData.category) grouped[name].levels[levelData.id].category = levelData.category;
      if (levelData.cat) grouped[name].levels[levelData.id].cat = levelData.cat;
      if (levelData.group) grouped[name].levels[levelData.id].group = levelData.group;
      if (Array.isArray(levelData.tags) && levelData.tags.length) {
        grouped[name].levels[levelData.id].tags = levelData.tags;
      }
    }
  }

  return grouped;
};

const buildLevelsAggFromRows = (rows) => {
  const levelsAgg = {};

  for (const row of rows) {
    const seenInThisStudent = new Set();

    for (const [levelKey, rawLevelData] of Object.entries(
      normalizeLevelsMap(row.levels)
    )) {
      const levelData = normalizeLevelEntry(levelKey, rawLevelData);
      if (!levelData) continue;

      if (!levelsAgg[levelData.id]) {
        levelsAgg[levelData.id] = {
          id: levelData.id,
          levelId: levelData.levelId,
          title: levelData.title,
          subtitle: levelData.subtitle,
          category: levelData.category,
          cat: levelData.cat,
          group: levelData.group,
          tags: levelData.tags,
          sessions: 0,
          completed: 0,
          totalMs: 0,
          activeStudents: 0,
        };
      }

      levelsAgg[levelData.id].sessions += levelData.sessions;
      levelsAgg[levelData.id].completed += levelData.completed;
      levelsAgg[levelData.id].totalMs += levelData.totalMs;

      if (levelData.title) levelsAgg[levelData.id].title = levelData.title;
      if (levelData.subtitle) levelsAgg[levelData.id].subtitle = levelData.subtitle;
      if (levelData.category) levelsAgg[levelData.id].category = levelData.category;
      if (levelData.cat) levelsAgg[levelData.id].cat = levelData.cat;
      if (levelData.group) levelsAgg[levelData.id].group = levelData.group;
      if (Array.isArray(levelData.tags) && levelData.tags.length) {
        levelsAgg[levelData.id].tags = levelData.tags;
      }

      const hasAnyUsage =
        levelData.sessions > 0 ||
        levelData.completed > 0 ||
        levelData.totalMs > 0;

      if (hasAnyUsage && !seenInThisStudent.has(levelData.id)) {
        seenInThisStudent.add(levelData.id);
        levelsAgg[levelData.id].activeStudents += 1;
      }
    }
  }

  return levelsAgg;
};

export default {
  namespaced: true,

  state: () => ({
    loading: false,
    error: "",
    teacherId: "",
    groupId: "",
    groupName: "",
    month: "",
    students: [],
    studentRows: [],
    statsByName: {},
    levelsAgg: {},
    lastFetchAt: 0,
  }),

  mutations: {
    setLoading(state, value) {
      state.loading = !!value;
    },

    setError(state, value) {
      state.error = value || "";
    },

    setTeacherId(state, value) {
      state.teacherId = value || "";
    },

    setGroupId(state, value) {
      state.groupId = value || "";
    },

    setGroupName(state, value) {
      state.groupName = value || "";
    },

    setMonth(state, value) {
      state.month = value || "";
    },

    setStudents(state, value) {
      state.students = Array.isArray(value) ? value : [];
    },

    setStudentRows(state, value) {
      state.studentRows = Array.isArray(value) ? value : [];
    },

    setStatsByName(state, value) {
      state.statsByName =
        value && typeof value === "object" && !Array.isArray(value) ? value : {};
    },

    setLevelsAgg(state, value) {
      state.levelsAgg =
        value && typeof value === "object" && !Array.isArray(value) ? value : {};
    },

    setLastFetch(state, value) {
      state.lastFetchAt = value || 0;
    },

    clearStats(state) {
      state.students = [];
      state.studentRows = [];
      state.statsByName = {};
      state.levelsAgg = {};
      state.groupId = "";
      state.groupName = "";
      state.lastFetchAt = 0;
    },
  },

  actions: {
    async fetch({ commit }, { userId, month = isoLocalMonth() } = {}) {
      if (!userId) {
        commit("setTeacherId", "");
        commit("setError", "Missing userId");
        commit("setMonth", isValidMonth(month) ? String(month) : isoLocalMonth());
        commit("clearStats");
        commit("setLoading", false);
        return;
      }

      const safeMonth = isValidMonth(month) ? String(month) : isoLocalMonth();
      const requestId = ++activeRequestId;

      try {
        commit("setLoading", true);
        commit("setError", "");
        commit("setTeacherId", userId);
        commit("setMonth", safeMonth);

        const userRef = doc(db, "users", userId);
        const userSnap = await getDoc(userRef);

        if (requestId !== activeRequestId) return;

        if (!userSnap.exists()) {
          throw new Error("Teacher user not found");
        }

        const userData = userSnap.data() || {};

        if (userData.role !== "teacher") {
          throw new Error("User is not a teacher");
        }

        const groupId =
          toTrimmedString(userData.groupId) ||
          toTrimmedString(userData.groupID) ||
          toTrimmedString(userData?.group?.id);

        if (!groupId) {
          throw new Error("Teacher does not have a groupId");
        }

        commit("setGroupId", groupId);

        const groupRef = doc(db, "groups", groupId);
        const groupSnap = await getDoc(groupRef);

        if (requestId !== activeRequestId) return;

        if (!groupSnap.exists()) {
          throw new Error("Group not found");
        }

        const groupData = groupSnap.data() || {};
        const groupName =
          toTrimmedString(groupData.groupName) ||
          toTrimmedString(groupData.name) ||
          "";

        commit("setGroupName", groupName);

        const students = normalizeStudentsMap(groupData.students);
        commit("setStudents", students);

        if (students.length === 0) {
          commit("setStudentRows", []);
          commit("setStatsByName", {});
          commit("setLevelsAgg", {});
          commit("setLastFetch", Date.now());
          return;
        }

        const refs = students.map(({ kidId }) =>
          doc(db, "monthlyStats", buildMonthlyStatDocId(kidId, safeMonth))
        );

        const snaps = await Promise.all(refs.map((ref) => getDoc(ref)));

        if (requestId !== activeRequestId) return;

        const rows = students
          .map(({ kidId, accountId, studentName }, index) => {
            const base = buildEmptyStudentRow({
              kidId,
              accountId,
              studentName,
              month: safeMonth,
            });

            const snap = snaps[index];

            if (!snap || !snap.exists()) {
              return base;
            }

            const data = snap.data() || {};

            const statKidId =
              toTrimmedString(data.kidId) ||
              toTrimmedString(data.studentId) ||
              toTrimmedString(data.userId);

            if (statKidId && statKidId !== kidId) {
              return base;
            }

            if (data.month && data.month !== safeMonth) {
              return base;
            }

            return {
              ...base,
              id: snap.id,
              userId: toTrimmedString(data.userId) || accountId || "",
              accountId: toTrimmedString(data.accountId) || accountId || "",
              kidId,
              studentId: kidId,
              activeDays: toNumber(data.activeDays),
              sessions: toNumber(data.sessions),
              completed: toNumber(data.completed),
              totalMs: toNumber(data.totalMs),
              createdAt: data.createdAt || null,
              updatedAt: data.updatedAt || null,
              levels: normalizeLevelsMap(data.levels),
            };
          })
          .sort((a, b) =>
            (a.studentName || "").localeCompare(b.studentName || "", undefined, {
              sensitivity: "base",
            })
          );

        const statsByName = buildStatsByName(rows);
        const levelsAgg = buildLevelsAggFromRows(rows);

        commit("setStudentRows", rows);
        commit("setStatsByName", statsByName);
        commit("setLevelsAgg", levelsAgg);
        commit("setLastFetch", Date.now());
      } catch (err) {
        console.error("[groupDashboardStats] fetch error", err);

        if (requestId !== activeRequestId) return;

        commit("setError", err?.message || String(err));
        commit("setStudents", []);
        commit("setStudentRows", []);
        commit("setStatsByName", {});
        commit("setLevelsAgg", {});
        commit("setGroupId", "");
        commit("setGroupName", "");
        commit("setLastFetch", 0);
      } finally {
        if (requestId === activeRequestId) {
          commit("setLoading", false);
        }
      }
    },
  },

  getters: {
    groupTotals(state) {
      const sessions = state.studentRows
        .map((row) => toNumber(row.sessions))
        .reduce(sum, 0);

      const completed = state.studentRows
        .map((row) => toNumber(row.completed))
        .reduce(sum, 0);

      const totalMs = state.studentRows
        .map((row) => toNumber(row.totalMs))
        .reduce(sum, 0);

      const activeDays = state.studentRows
        .map((row) => toNumber(row.activeDays))
        .reduce(sum, 0);

      return {
        activeDays,
        sessions,
        completed,
        totalMs,
      };
    },

    groupedStudents(state) {
      return Object.values(state.statsByName || {}).sort((a, b) => {
        if (b.sessions !== a.sessions) return b.sessions - a.sessions;
        if (b.completed !== a.completed) return b.completed - a.completed;
        return b.totalMs - a.totalMs;
      });
    },

    topStudents(state) {
      return [...state.studentRows].sort((a, b) => {
        if (b.sessions !== a.sessions) return b.sessions - a.sessions;
        if (b.completed !== a.completed) return b.completed - a.completed;
        return b.totalMs - a.totalMs;
      });
    },

    topLevelsMonth(state) {
      return Object.values(state.levelsAgg || {}).sort((a, b) => {
        if (b.sessions !== a.sessions) return b.sessions - a.sessions;
        if (b.completed !== a.completed) return b.completed - a.completed;
        return b.totalMs - a.totalMs;
      });
    },

    kidById: (state) => (kidId) => {
      return state.studentRows.find((row) => row.kidId === kidId) || null;
    },

    studentById: (state, getters) => (studentId) => {
      return getters.kidById(studentId);
    },

    statsForName: (state) => (name) => {
      return state.statsByName?.[name] || null;
    },

    studentSeries(state) {
      return state.studentRows.map((row) => ({
        x: row.studentName,
        y: toNumber(row.sessions),
        kidId: row.kidId,
        studentId: row.kidId,
        completed: toNumber(row.completed),
        totalMs: toNumber(row.totalMs),
        activeDays: toNumber(row.activeDays),
      }));
    },

    hasData(state) {
      return state.studentRows.length > 0;
    },
  },
};